import { useEffect } from "react"

import usePrevious from "@p-ban/common/src/hooks/usePrevious"
import { shallowDiff } from "@p-ban/common/src/utils/ObjectUtils"

import notify, { clearPrevNotification } from "../../../../../utils/notify"
import getConstantByValue from "../../../service/constant/getConstantByValue"
import estimateField, { isRadioField } from "../../../service/estimateFields/estimateField"
import fillRestWithInitialValues from "../../../service/fillRestWithInitialValues"
import resolveResistPrintSideName from "../../../service/sideNameResolver/resolveResistPrintSideName"
import resolveSilkPrintSideName from "../../../service/sideNameResolver/resolveSilkPrintSideName"
import type { Touched } from "../../../types"
import type EstimateValues from "../../../types/params"
import getNoResetValues from "../getNoResetValues"

/**
 * フォームの状態が変わったときの通知処理を行います。
 */
const useNotify = (values: EstimateValues, lastTouchedField = "", touched: Touched) => {
  // 値をリセットしたときにtouchedの内容もリセットされてしまうので前回の状態を使う
  const prevTouched = usePrevious(touched)
  const prevValues = usePrevious(values)

  useEffect(() => {
    if (lastTouchedField) {
      clearPrevNotification?.()
    }
    if (!prevValues || !lastTouchedField || !prevTouched) return

    const message =
      lastTouchedField === estimateField.kibanType.key
        ? // 基板種別を変更した場合、フォームの状態がリセットされるので専用のメッセージを作成します。
          createResetMessage(prevValues, fillRestWithInitialValues)
        : createMessage(prevValues, values, lastTouchedField, prevTouched)

    if (message) {
      notify(message)
    }

    // フォームの状態が変更された場合のみ通知処理を行なう。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])
}

export default useNotify

const createResetMessage = (
  prevValues: EstimateValues,
  fillRestWithInitialValues: (partialValues: Partial<EstimateValues>) => EstimateValues
) => {
  const prevInitialValues = fillRestWithInitialValues({
    kibanType: prevValues.kibanType,
    ...getNoResetValues(prevValues),
  })

  // リセット前にユーザーによるフォームの操作があった場合に通知をします。
  // 基板種別を変更する前のフォームとその基板種別での初期状態を比較し、
  // 差分があった場合はユーザーによるフォームの操作があったとみなし通知をします。
  const shouldNotify = Object.keys(shallowDiff(prevInitialValues, prevValues)).length > 0

  if (shouldNotify) {
    return "すべての見積項目をリセットしました。"
  }
}
const createMessage = (
  values: EstimateValues,
  nextValues: EstimateValues,
  lastTouchedField: string,
  touched: Touched
) => {
  const messages: string[] = []
  const diff = shallowDiff(values, nextValues)

  const keys = Object.keys(diff) as (keyof typeof diff)[]
  for (const name of keys) {
    if (lastTouchedField === name) continue
    if (!isRadioField(name)) continue
    const currentDiff = diff[name]
    if (!currentDiff) continue
    if (!touched[name]) continue

    // 塗布する面と印刷する面はコード値が同一で表示名が異なる場合があるため、正しい表示名を取得する
    const resolveName =
      name === estimateField.resistPrint.key
        ? resolveResistPrintSideName
        : name === estimateField.silk.key
        ? resolveSilkPrintSideName
        : null

    const prevDisplayName = resolveName?.(currentDiff.from, values) ?? getConstantByValue(name, currentDiff.from).name
    const currentDisplayName = resolveName?.(currentDiff.to, values) ?? getConstantByValue(name, currentDiff.to).name

    if (prevDisplayName !== currentDisplayName) {
      const fieldDisplayName = estimateField[name].displayName(nextValues)
      messages.push(`${fieldDisplayName} : [${prevDisplayName}] → [${currentDisplayName}]`)
    }
  }

  if (messages.length > 0) {
    messages.unshift("以下の選択肢を変更しました。")
  }

  return messages.join("\n")
}
